// Maten: the day's menu with real recipes, the week ahead, and the shopping
// list that falls out of it. The menu is an OFFER, never a quota — the
// priority order behind it (tolerability > calories > protein > quality)
// lives in skills/kitchen/references/diet-targets.md, and the copy here says
// the honest version: half a portion that goes down beats a full one that
// stands. Soft days (öm mun) are marked and the whole menu already respects
// them — the server decided, this view just shows it.
//
// The shopping list checks off locally (localStorage, keyed by week start):
// crossing off "grädde" is not patient data, it is a kitchen note.

import { getMenu, getShoppingList, type MenuDay, type ShoppingGroup } from "../api";
import { clear, el } from "../dom";
import { getLang, t } from "../strings";

const SLOT_LABEL: Record<string, Parameters<typeof t>[0]> = {
  frukost: "mat.slot.frukost",
  mellanmal_fm: "mat.slot.mellanmal",
  lunch: "mat.slot.lunch",
  mellanmal_em: "mat.slot.mellanmal",
  middag: "mat.slot.middag",
};

function fmtDate(date: string, opts: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(getLang() === "sv" ? "sv-SE" : "en-GB", {
    timeZone: "UTC",
    ...opts,
  }).format(new Date(`${date}T12:00:00Z`));
}

function fmtQty(qty: number): string {
  const text = String(Math.round(qty * 10) / 10);
  return getLang() === "sv" ? text.replace(".", ",") : text;
}

export function mountMat(root: HTMLElement): () => void {
  let menuDays: MenuDay[] = [];
  let proteinTarget: [number, number] | null = null;
  let safetyLine = "";
  let shopping: { from: string; to: string; groups: ShoppingGroup[] } | null = null;
  let failed = false;
  const openMeals = new Set<string>();
  let disposed = false;

  // Checked-off shopping rows live per list window, so a new week starts clean.
  let checked = new Set<string>();
  let checkedKey = "";
  function loadChecked(from: string): void {
    checkedKey = `phoenix.shop.${from}`;
    try {
      checked = new Set(JSON.parse(localStorage.getItem(checkedKey) ?? "[]") as string[]);
    } catch {
      checked = new Set();
    }
  }
  function saveChecked(): void {
    try {
      localStorage.setItem(checkedKey, JSON.stringify([...checked]));
    } catch {
      /* storage full or blocked — the list still works, just forgets */
    }
  }

  async function load(): Promise<void> {
    try {
      const [menu, shop] = await Promise.all([getMenu(7), getShoppingList(7)]);
      if (disposed) return;
      menuDays = menu.days;
      proteinTarget = menu.protein_target_g;
      safetyLine = menu.safety_sv ?? "";
      shopping = shop;
      loadChecked(shop.from);
      failed = false;
    } catch {
      failed = true;
    }
    render();
  }

  function mealRow(day: MenuDay, meal: MenuDay["meals"][number]): HTMLElement {
    const key = `${day.date}:${meal.slot}`;
    const recipe = meal.recipe;
    const wrap = el("div", { class: "prog" });

    const row = el("div", { class: "prog-row" });
    const open = el("button", {
      class: "prog-open",
      type: "button",
      "aria-expanded": openMeals.has(key) ? "true" : "false",
    });
    const title = el("div", { class: "title meal-title" }, [
      el("span", { class: "prog-when", text: t(SLOT_LABEL[meal.slot] ?? "mat.slot.mellanmal") }),
      el("span", { class: "prog-label", text: recipe.title_sv }),
      el("span", {
        class: "muted small",
        text: `ca ${recipe.kcal} kcal · ${recipe.protein_g} g protein`,
      }),
    ]);
    open.append(title, el("span", { class: "chev", "aria-hidden": "true", text: "▾" }));
    open.addEventListener("click", () => {
      if (openMeals.has(key)) openMeals.delete(key);
      else openMeals.add(key);
      render();
    });
    row.append(open);
    wrap.append(row);

    if (openMeals.has(key)) {
      const detail = el("div", { class: "prog-detail" });
      detail.append(el("p", { class: "prog-why", text: recipe.why_sv }));
      detail.append(el("h4", { class: "prog-when", text: t("mat.detail.ingredients") }));
      const list = el("ul", { class: "shop-list" });
      for (const ing of recipe.ingredients) {
        list.append(el("li", { text: `${fmtQty(ing.qty)} ${ing.unit === "st" || ing.unit === "port" ? "" : ing.unit + " "}${ing.name}`.replace(/\s+/g, " ") }));
      }
      detail.append(list);
      detail.append(el("h4", { class: "prog-when", text: t("mat.detail.steps") }));
      detail.append(el("p", { class: "prog-how", text: recipe.steps_sv.join("\n") }));
      if (recipe.tip_sv) detail.append(el("p", { class: "prog-why", text: recipe.tip_sv }));
      wrap.append(detail);
    }
    return wrap;
  }

  function todaySection(): HTMLElement | null {
    const today = menuDays[0];
    if (!today) return null;
    const card = el("div", { class: "card" });
    card.append(el("h2", { text: t("mat.today.title") }));
    if (today.soft) card.append(el("p", { class: "muted small", text: t("mat.today.soft") }));
    const box = el("div", { class: "prog" });
    for (const meal of today.meals) box.append(mealRow(today, meal));
    card.append(box);
    const target =
      proteinTarget !== null
        ? ` ${t("mat.today.target").replace("%LO%", String(proteinTarget[0])).replace("%HI%", String(proteinTarget[1]))}`
        : "";
    card.append(
      el("p", {
        class: "muted small",
        text: `${t("mat.today.totals")
          .replace("%KCAL%", String(today.kcal_total))
          .replace("%PROT%", String(today.protein_total_g))}${target} ${t("mat.today.noquota")}`,
      })
    );
    return card;
  }

  function weekSection(): HTMLElement | null {
    if (menuDays.length < 2) return null;
    const card = el("div", { class: "card" });
    card.append(el("h2", { text: t("mat.week.title") }));
    for (const day of menuDays.slice(1)) {
      const dinner = day.meals.find((m) => m.slot === "middag");
      if (!dinner) continue;
      const line = el("p", { class: "week-line" }, [
        el("span", { class: "prog-when", text: fmtDate(day.date, { weekday: "long" }) }),
        document.createTextNode(` ${dinner.recipe.title_sv}`),
      ]);
      if (day.soft) line.append(el("span", { class: "muted small", text: ` · ${t("mat.week.soft")}` }));
      card.append(line);
    }
    return card;
  }

  function shoppingSection(): HTMLElement | null {
    if (!shopping) return null;
    const card = el("div", { class: "card" });
    card.append(
      el("h2", { text: t("mat.shop.title") }),
      el("p", { class: "muted small", text: t("mat.shop.hint") })
    );
    for (const group of shopping.groups) {
      card.append(el("h4", { class: "prog-when shop-cat", text: group.label_sv }));
      const list = el("div", { class: "shop" });
      for (const item of group.items) {
        const key = `${item.name}|${item.unit}`;
        const row = el("button", {
          class: `shop-row${checked.has(key) ? " done" : ""}`,
          type: "button",
          "aria-pressed": checked.has(key) ? "true" : "false",
        });
        row.append(
          el("span", { class: "shop-box", "aria-hidden": "true" }),
          el("span", {
            class: "shop-name",
            text: `${item.name} — ${fmtQty(item.qty)}${item.unit === "st" || item.unit === "port" ? " st" : " " + item.unit}`,
          })
        );
        row.addEventListener("click", () => {
          if (checked.has(key)) checked.delete(key);
          else checked.add(key);
          saveChecked();
          render();
        });
        list.append(row);
      }
      card.append(list);
    }
    const reset = el("button", { class: "btn ghost small", type: "button", text: t("mat.shop.reset") });
    reset.addEventListener("click", () => {
      checked.clear();
      saveChecked();
      render();
    });
    card.append(reset);
    return card;
  }

  function render(): void {
    clear(root);
    const wrap = el("div", { class: "view" });
    wrap.append(
      el("header", { class: "hero" }, [
        el("h1", { text: t("mat.title") }),
        el("p", { class: "muted", text: t("mat.hint") }),
      ])
    );
    if (failed) {
      const cardFail = el("div", { class: "card" });
      cardFail.append(el("p", { class: "muted", text: t("mat.failed") }));
      const retry = el("button", { class: "btn", type: "button", text: t("mat.retry") });
      retry.addEventListener("click", () => void load());
      cardFail.append(retry);
      wrap.append(cardFail);
    } else if (menuDays.length === 0) {
      wrap.append(el("div", { class: "card" }, [el("p", { class: "muted", text: t("mat.loading") })]));
    } else {
      const sections = [todaySection(), weekSection(), shoppingSection()];
      for (const section of sections) if (section) wrap.append(section);
      if (safetyLine) wrap.append(el("p", { class: "muted small", text: safetyLine }));
    }
    root.append(wrap);
  }

  render();
  void load();
  return () => {
    disposed = true;
  };
}
